import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import { fetchProject, renameProject, deleteProject } from '../../../../flux/actions/projectsThunks';
import { clearCurrentProject, clearMutationError } from '../../../../flux/stores/projectsReducer';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { ProjectOverview } from '../ProjectOverview/ProjectOverview';
import { ApsConnectionPanel } from '../ApsConnectionPanel/ApsConnectionPanel';
import { ModelGroupsPanel } from '../ModelGroupsPanel/ModelGroupsPanel';
import { ProjectRules } from '../ProjectRules/ProjectRules';
import { ProjectChecks } from '../ProjectChecks/ProjectChecks';
import './ProjectDetail.css';

// The whole Autodesk surface is org_admin only, reads included, so a member is
// not shown a tab they would only get a 403 from. Active rules, by contrast,
// are readable by any project member — only activating/deactivating one is
// admin-only, gated inside the tab itself. Checks are org_admin only too,
// matching the backend's require_project_admin() on every rule-evaluations
// route.
const OVERVIEW_TAB = { id: 'overview', label: 'Overview' };
const RULES_TAB = { id: 'rules', label: 'Active rules' };
const AUTODESK_TAB = { id: 'autodesk', label: 'Autodesk' };
const CHECKS_TAB = { id: 'checks', label: 'Checks' };

export const ProjectDetail = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();

    const currentUser = useAppSelector(state => state.user.current.data);
    const project = useAppSelector(state => state.projects.current.data);
    const isLoading = useAppSelector(state => state.projects.current.isLoading);
    const loadError = useAppSelector(state => state.projects.current.error);
    const mutation = useAppSelector(state => state.projects.mutation);

    const [tab, setTab] = useState('overview');
    const [renameValue, setRenameValue] = useState(null);
    const [isConfirmingDelete, setConfirmingDelete] = useState(false);

    const isAdmin = currentUser?.org_role === 'admin';
    const tabs = isAdmin ? [OVERVIEW_TAB, RULES_TAB, AUTODESK_TAB, CHECKS_TAB] : [OVERVIEW_TAB, RULES_TAB];
    // Losing admin standing mid-session must not strand the page on a tab that
    // no longer exists.
    const activeTab = tabs.some(t => t.id === tab) ? tab : OVERVIEW_TAB.id;

    const loadProject = useCallback(async () => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            dispatch(fetchProject({ accessToken, projectId }));
        } catch (error) {
            console.error('Failed to acquire API token for the project:', error);
        }
    }, [dispatch, getAccessToken, projectId]);

    useEffect(() => {
        loadProject();
    }, [loadProject]);

    // Leaving the page drops the loaded project, so opening a different one
    // never shows the previous project's name while its fetch is in flight.
    useEffect(() => () => { dispatch(clearCurrentProject()); }, [dispatch]);

    const startRename = () => {
        dispatch(clearMutationError());
        setRenameValue(project.name);
    };

    const submitRename = async (event) => {
        event.preventDefault();
        const name = renameValue.trim();
        if (!name || name === project.name) {
            setRenameValue(null);
            return;
        }
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            const result = await dispatch(renameProject({ accessToken, projectId, name }));
            if (renameProject.fulfilled.match(result)) setRenameValue(null);
        } catch (error) {
            console.error('Failed to rename the project:', error);
        }
    };

    const confirmDelete = async () => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            const result = await dispatch(deleteProject({ accessToken, projectId }));
            if (deleteProject.fulfilled.match(result)) {
                setConfirmingDelete(false);
                navigate('/projects');
            }
        } catch (error) {
            console.error('Failed to delete the project:', error);
        }
    };

    if (isLoading && !project) {
        return (
            <div className="page-content">
                <p className="projects-loading">Loading project…</p>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="page-content">
                <button type="button" className="pw-back" onClick={() => navigate('/projects')}>
                    ← Back to Projects
                </button>
                <div className="rules-empty">
                    <p>
                        {loadError?.status === 403
                            ? 'You do not have access to this project.'
                            : loadError?.status === 404
                                ? 'This project no longer exists.'
                                : loadError?.message ?? 'The project could not be loaded.'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <button type="button" className="pw-back" onClick={() => navigate('/projects')}>
                ← Back to Projects
            </button>

            <div className="pd-header">
                {renameValue === null ? (
                    <div className="pd-header-main">
                        <h1 className="rules-title">{project.name}</h1>
                        {isAdmin && (
                            <div className="pd-header-actions">
                                <button type="button" className="rules-btn-ghost" onClick={startRename}>
                                    Rename
                                </button>
                                <button
                                    type="button"
                                    className="rules-btn-ghost pd-danger"
                                    onClick={() => { dispatch(clearMutationError()); setConfirmingDelete(true); }}
                                >
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <form className="pd-rename-form" onSubmit={submitRename}>
                        <input
                            className="pw-input"
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            disabled={mutation.isSaving}
                            aria-label="Project name"
                            autoFocus
                        />
                        <button type="submit" className="rules-btn-primary" disabled={mutation.isSaving}>
                            {mutation.isSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            type="button"
                            className="rules-btn-ghost"
                            onClick={() => setRenameValue(null)}
                            disabled={mutation.isSaving}
                        >
                            Cancel
                        </button>
                    </form>
                )}
            </div>

            {mutation.error && !isConfirmingDelete && <p className="org-error">{mutation.error.message}</p>}

            <div className="pd-tabs">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        type="button"
                        className={`pd-tab${activeTab === t.id ? ' active' : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {activeTab === 'overview' && <ProjectOverview project={project} isAdmin={isAdmin} />}

            {activeTab === 'rules' && <ProjectRules projectId={projectId} isAdmin={isAdmin} />}

            {activeTab === 'autodesk' && (
                <>
                    <ApsConnectionPanel
                        projectId={projectId}
                        currentUserObjectId={currentUser?.object_id}
                    />
                    <ModelGroupsPanel projectId={projectId} />
                </>
            )}

            {activeTab === 'checks' && <ProjectChecks projectId={projectId} />}

            {isConfirmingDelete && (
                <div className="members-modal-backdrop" role="dialog" aria-modal="true" aria-label="Delete project">
                    <div className="members-modal">
                        <h2>Delete "{project.name}"?</h2>
                        <p>
                            This removes the project, its members, its pending invitations, its rule
                            assignments and its Autodesk connection. The rules themselves belong to the
                            organization and are not affected.
                        </p>
                        {mutation.error && <p className="org-error">{mutation.error.message}</p>}
                        <div className="pw-actions">
                            <button
                                type="button"
                                className="rules-btn-primary pd-danger-btn"
                                onClick={confirmDelete}
                                disabled={mutation.isSaving}
                            >
                                {mutation.isSaving ? 'Deleting…' : 'Delete project'}
                            </button>
                            <button
                                type="button"
                                className="rules-btn-ghost"
                                onClick={() => setConfirmingDelete(false)}
                                disabled={mutation.isSaving}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
